import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { useLocale } from "@/contexts/LocaleContext";
import api, { formatApiError } from "@/lib/api";
import MockedBadge from "@/components/MockedBadge";
import { ArrowRight, ArrowLeft, Building2, Users2, Tablet, Sparkles, CheckCircle2, Database } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { key: "welcome", label: "Welcome", icon: Sparkles },
  { key: "site", label: "First site", icon: Building2 },
  { key: "host", label: "Invite a host", icon: Users2 },
  { key: "kiosk", label: "Pair kiosk", icon: Tablet },
  { key: "done", label: "Go live", icon: CheckCircle2 },
];

export default function OnboardingWizard() {
  const { formatTime } = useLocale();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [checklist, setChecklist] = useState({});
  const [sites, setSites] = useState([]);
  const [kiosk, setKiosk] = useState(null);

  const [site, setSite] = useState({ name: "", address: "", postcode: "" });
  const [host, setHost] = useState({ email: "", name: "", role: "host" });

  const [saving, setSaving] = useState(false);

  // 18.4 — Kiosk pairing walkthrough: poll for pair status while step 3 is active
  useEffect(() => {
    if (step !== 3 || !kiosk?.id) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/kiosks/${kiosk.id}/status`);
        if (!cancelled && data.paired_at) {
          setKiosk(data);
          await api.patch("/onboarding/checklist", { items: { kiosk_pairing_viewed: true } });
          setChecklist((c) => ({ ...c, kiosk_pairing_viewed: true }));
          toast.success("Kiosk paired");
          // small delay then auto-advance
          setTimeout(() => setStep(4), 1500);
        }
      } catch (e) { console.warn("[OnboardingWizard] kiosk pair-status poll failed", e); }
    };
    const id = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [step, kiosk?.id]);

  useEffect(() => {
    (async () => {
      try {
        const [cl, s] = await Promise.all([api.get("/onboarding/checklist"), api.get("/sites")]);
        setChecklist(cl.data.items || {});
        setSites(s.data);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);

  const markWelcome = async () => {
    await api.patch("/onboarding/checklist", { items: { welcome: true } });
    setChecklist((c) => ({ ...c, welcome: true }));
    setStep(1);
  };

  const createSite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/sites", { ...site, opening_hours: { mon: "09:00-17:00" } });
      setSites((prev) => [data, ...prev]);
      setChecklist((c) => ({ ...c, first_site: true }));
      toast.success("Site created");
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const inviteHost = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/invitations", host);
      await api.patch("/onboarding/checklist", { items: { first_host_invited: true } });
      setChecklist((c) => ({ ...c, first_host_invited: true }));
      toast.success("Invitation sent (MOCKED — view in Notifications log)");
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const pairKiosk = async () => {
    const siteId = sites[0]?.id;
    if (!siteId) {
      toast.error("Create a site first");
      setStep(1);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/kiosks/pairing", { site_id: siteId, name: "Reception iPad" });
      setKiosk(data);
      setChecklist((c) => ({ ...c, kiosk_pairing_viewed: true }));
      await api.patch("/onboarding/checklist", { items: { kiosk_pairing_viewed: true } });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const loadDemo = async () => {
    setSaving(true);
    try {
      await api.post("/onboarding/load-sample-data");
      const [cl, s] = await Promise.all([api.get("/onboarding/checklist"), api.get("/sites")]);
      setChecklist(cl.data.items || {});
      setSites(s.data);
      toast.success("Sample data loaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto" data-testid="onboarding-wizard">
        <div className="flex items-center gap-2 mb-2">
          <div className="eb-overline">Setup</div>
          <MockedBadge service="resend" label="Resend" />
          <MockedBadge service="stripe" label="Stripe" />
        </div>
        <h1 className="text-3xl font-semibold mb-1">Onboarding wizard</h1>
        <p className="text-sm text-navy-500">A guided 4-step setup — takes ~3 minutes. Invitation emails and billing are MOCKED in Phase 0.</p>

        {/* Stepper */}
        <div className="flex items-center gap-2 mt-8 mb-10" data-testid="wizard-stepper">
          {STEPS.map((s, i) => (
            <div
              key={s.key}
              className={`flex-1 h-2 rounded-full transition-all duration-500 ${
                i < step ? "bg-navy-900" : i === step ? "bg-orange-500" : "bg-navy-100"
              }`}
            />
          ))}
        </div>

        <div className="eb-card p-8 animate-fade-in-up">
          {/* Step 0: welcome */}
          {step === 0 && (
            <div data-testid="wizard-step-welcome">
              <h2 className="text-2xl font-semibold">Welcome to EntryBook</h2>
              <p className="mt-3 text-navy-600 text-sm leading-relaxed max-w-lg">
                We'll set up your first site, invite a host, and generate a kiosk pairing PIN for your tablet.
                You can always come back to the dashboard and finish later.
              </p>
              <div className="mt-6 grid md:grid-cols-2 gap-4 text-sm">
                <div className="border border-navy-100 rounded-lg p-4 bg-surface">
                  <div className="font-medium">Start from scratch</div>
                  <p className="text-navy-500 mt-1 text-xs">Create your own site and invite your own hosts.</p>
                  <button className="btn-accent mt-3" onClick={markWelcome} data-testid="wizard-scratch-btn">
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
                <div className="border border-orange-200 rounded-lg p-4 bg-orange-50/40">
                  <div className="font-medium">Or explore with demo data</div>
                  <p className="text-navy-500 mt-1 text-xs">We'll populate a sample Barking office with hosts, visits, a contractor & permit.</p>
                  <button className="btn-outline mt-3" onClick={loadDemo} disabled={saving} data-testid="wizard-load-demo-btn">
                    <Database className="w-4 h-4" /> Load sample data
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: site */}
          {step === 1 && (
            <form onSubmit={createSite} className="space-y-4" data-testid="wizard-step-site">
              <h2 className="text-2xl font-semibold">Create your first site</h2>
              <p className="text-sm text-navy-500">A site is a physical location (office, depot, warehouse).</p>
              <div>
                <label className="eb-label">Name</label>
                <input required value={site.name} onChange={(e) => setSite({ ...site, name: e.target.value })} className="eb-input" data-testid="wizard-site-name" placeholder="HQ — London" />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="eb-label">Address</label>
                  <input required value={site.address} onChange={(e) => setSite({ ...site, address: e.target.value })} className="eb-input" data-testid="wizard-site-address" />
                </div>
                <div>
                  <label className="eb-label">Postcode</label>
                  <input required value={site.postcode} onChange={(e) => setSite({ ...site, postcode: e.target.value })} className="eb-input" data-testid="wizard-site-postcode" placeholder="IG11 8HH" />
                </div>
              </div>
              <div className="flex justify-between pt-2">
                <button type="button" className="btn-ghost" onClick={() => setStep(0)} data-testid="wizard-site-back">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button type="submit" disabled={saving} className="btn-accent" data-testid="wizard-site-submit">
                  {saving ? "Saving…" : "Save & continue"} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          )}

          {/* Step 2: invite host */}
          {step === 2 && (
            <form onSubmit={inviteHost} className="space-y-4" data-testid="wizard-step-host">
              <h2 className="text-2xl font-semibold">Invite your first host</h2>
              <p className="text-sm text-navy-500">Hosts receive notifications when their visitors sign in.</p>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="eb-label">Name</label>
                  <input required value={host.name} onChange={(e) => setHost({ ...host, name: e.target.value })} className="eb-input" data-testid="wizard-host-name" placeholder="Olivia Hughes" />
                </div>
                <div>
                  <label className="eb-label">Email</label>
                  <input required type="email" value={host.email} onChange={(e) => setHost({ ...host, email: e.target.value })} className="eb-input" data-testid="wizard-host-email" placeholder="host@company.co.uk" />
                </div>
              </div>
              <div>
                <label className="eb-label">Role</label>
                <select value={host.role} onChange={(e) => setHost({ ...host, role: e.target.value })} className="eb-input" data-testid="wizard-host-role">
                  <option value="host">Host</option>
                  <option value="receptionist">Receptionist</option>
                  <option value="site_manager">Site manager</option>
                  <option value="org_admin">Org admin</option>
                </select>
              </div>
              <div className="text-xs text-navy-500 flex items-center gap-2">
                <span className="eb-mocked-badge">Mocked — Resend</span>
                The invitation email will appear in the Notifications log (no real email is sent in mock mode).
              </div>
              <div className="flex justify-between pt-2">
                <button type="button" className="btn-ghost" onClick={() => setStep(1)} data-testid="wizard-host-back">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button type="submit" disabled={saving} className="btn-accent" data-testid="wizard-host-submit">
                  {saving ? "Sending…" : "Send invitation"} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              <button type="button" onClick={() => setStep(3)} className="text-xs text-navy-500 hover:underline" data-testid="wizard-host-skip">
                Skip for now
              </button>
            </form>
          )}

          {/* Step 3: kiosk */}
          {step === 3 && (
            <div className="space-y-5" data-testid="wizard-step-kiosk">
              <h2 className="text-2xl font-semibold">Pair your reception tablet</h2>
              <p className="text-sm text-navy-500">
                Install EntryBook Kiosk on any tablet (coming in Phase 1). Enter the 6-digit PIN below when prompted.
              </p>
              {!kiosk ? (
                <button onClick={pairKiosk} disabled={saving} className="btn-accent" data-testid="wizard-generate-pin">
                  <Tablet className="w-4 h-4" /> Generate pairing PIN
                </button>
              ) : (
                <div className="border-2 border-orange-500 rounded-xl p-6 bg-orange-50/40 text-center" data-testid="kiosk-pin-box">
                  <div className="text-xs uppercase tracking-wider text-orange-600 font-semibold">Pairing PIN</div>
                  <div className="text-5xl font-bold tracking-[0.2em] mt-2" data-testid="kiosk-pin-value">{kiosk.pairing_pin || kiosk.pairing_code}</div>
                  <div className="text-xs text-navy-500 mt-2">Valid for 24 hours · Site: {sites.find((s) => s.id === kiosk.site_id)?.name || kiosk.site_id}</div>
                  <div className="mt-3 text-xs text-navy-600 break-all">Kiosk ID: <code className="bg-white/60 px-1.5 py-0.5 rounded">{kiosk.id}</code></div>
                  <div className="mt-4 text-xs text-navy-700 bg-white/70 rounded-md p-3 text-left">
                    <strong>Next step on the tablet:</strong> open <code className="bg-orange-100 px-1 rounded">/kiosk/pair</code> on your kiosk device, paste the Kiosk ID and the PIN above. We'll auto-advance once the device is paired.
                  </div>
                  {kiosk.paired_at && (
                    <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-success/15 border border-success/40 text-success text-xs font-semibold">
                      <CheckCircle2 className="w-3 h-3" /> Paired at {formatTime(kiosk.paired_at)}
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-between pt-2">
                <button type="button" className="btn-ghost" onClick={() => setStep(2)} data-testid="wizard-kiosk-back">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button type="button" className="btn-accent" onClick={() => setStep(4)} data-testid="wizard-kiosk-next">
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 4: done */}
          {step === 4 && (
            <div className="text-center space-y-4 py-5" data-testid="wizard-step-done">
              <div className="w-14 h-14 mx-auto bg-success/15 text-success rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h2 className="text-2xl font-semibold">You're ready to go.</h2>
              <p className="text-sm text-navy-500 max-w-lg mx-auto">
                Head to the dashboard to manage sites, hosts, and see who's signed in. You can return to this wizard any time.
              </p>
              <button className="btn-accent" onClick={() => nav("/dashboard")} data-testid="wizard-done-btn">
                Go to dashboard <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
